/**
 * Modale per importare i prodotti di un Company360 come Good360 ("Product360").
 *
 * Scarica i Good potenziali del Company360 (filtro GoodForNewCompany360) insieme all'eventuale
 * Good360 già collegato. Mostra una tabella: i prodotti già importati sono spuntati e disabilitati
 * (con un badge), gli altri sono selezionabili. Al salvataggio crea un Good360 per ogni prodotto
 * selezionato che non ne ha ancora uno.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Table,
	Badge,
} from 'reactstrap';
import { t } from '../utils/labels';
import { client } from '../utils/client';
import { Spinner } from './MainContentSpinner';
import { ImportGoods360Query } from '../graphql/query/ImportGoods360Query';
import { Good360CreateMutation } from '../graphql/mutations/Good360CreateMutation';
import { GOOD_360_CREATE_DEFAULTS } from '../utils/entities/good360';

interface ImportGoods360ModalProps {
	company360Id: string;
	isOpen: boolean;
	onClose: () => void;
	onSaved?: () => void;
}

interface PotentialGood {
	id: string;
	localId: number;
	name: string;
	imported: boolean;
}

export const ImportGoods360Modal: React.FC<ImportGoods360ModalProps> = ({
	company360Id,
	isOpen,
	onClose,
	onSaved,
}) => {
	const [goods, setGoods] = useState<PotentialGood[]>([]);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	const load = useCallback(async () => {
		if (!company360Id) return;
		setLoading(true);
		try {
			const all: PotentialGood[] = [];
			let cursor: string | null = null;
			// eslint-disable-next-line no-constant-condition
			do {
				const res = await client.query<any, any>({
					query: ImportGoods360Query,
					variables: { company360Id, cursor },
					fetchPolicy: 'network-only',
				});
				const conn = res.data.goods;
				(conn.edges || []).forEach((e: any) => {
					const node = e.node;
					all.push({
						id: node.id,
						localId: node.localId,
						name: node.name,
						imported: !!node.Good360,
					});
				});
				cursor = conn.pageInfo.hasNextPage ? conn.pageInfo.endCursor : null;
			} while (cursor);

			setGoods(all);
			// di default seleziono tutti i prodotti (già importati inclusi, per coerenza visiva);
			// quelli importabili partono spuntati così il caso comune "importa tutto" è un solo click
			setSelectedIds(new Set(all.map((g) => g.id)));
		} finally {
			setLoading(false);
		}
	}, [company360Id]);

	useEffect(() => {
		if (isOpen) {
			load();
		}
	}, [isOpen, load]);

	const toggle = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const importable = goods.filter((g) => !g.imported);
	const allImportableSelected =
		importable.length > 0 && importable.every((g) => selectedIds.has(g.id));

	const toggleAllImportable = () => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allImportableSelected) {
				importable.forEach((g) => next.delete(g.id));
			} else {
				importable.forEach((g) => next.add(g.id));
			}
			return next;
		});
	};

	const toCreate = importable.filter((g) => selectedIds.has(g.id));

	const save = async () => {
		setSaving(true);
		try {
			await Promise.all(
				toCreate.map((g) =>
					client.mutate({
						mutation: Good360CreateMutation,
						variables: {
							values: {
								...GOOD_360_CREATE_DEFAULTS,
								Good: { id: g.id },
							},
						},
					}),
				),
			);
			if (onSaved) {
				onSaved();
			} else {
				onClose();
			}
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal isOpen={isOpen} toggle={onClose} size="lg">
			<ModalHeader toggle={onClose}>{t`ImportGoods360/title`}</ModalHeader>
			<ModalBody>
				{loading ? (
					<Spinner show={true} />
				) : goods.length === 0 ? (
					<p className="text-muted">{t`ImportGoods360/empty`}</p>
				) : (
					<Table hover>
						<thead>
							<tr>
								<th style={{ width: 40 }}>
									<input
										type="checkbox"
										checked={allImportableSelected}
										disabled={importable.length === 0}
										onChange={toggleAllImportable}
									/>
								</th>
								<th>{t`ImportGoods360/good`}</th>
								<th style={{ width: 100 }}>{t`Good360/localId/label`}</th>
								<th style={{ width: 160 }}>{t`ImportGoods360/status`}</th>
							</tr>
						</thead>
						<tbody>
							{goods.map((g) => (
								<tr key={g.id}>
									<td>
										<input
											type="checkbox"
											checked={selectedIds.has(g.id)}
											disabled={g.imported}
											onChange={() => toggle(g.id)}
										/>
									</td>
									<td>{g.name}</td>
									<td>{g.localId}</td>
									<td>
										{g.imported ? (
											<Badge color="secondary">
												{t`ImportGoods360/alreadyImported`}
											</Badge>
										) : (
											<Badge color="success">
												{t`ImportGoods360/importable`}
											</Badge>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				)}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" disabled={saving} onClick={onClose}>
					{t`cancel`}
				</Button>
				<Button
					color="primary"
					disabled={saving || loading || toCreate.length === 0}
					onClick={save}
				>
					{saving
						? t`ImportGoods360/saving`
						: `${t`ImportGoods360/import`} (${toCreate.length})`}
				</Button>
			</ModalFooter>
		</Modal>
	);
};